import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { fmtCAD } from "@/lib/format";
import { AirplaneTakeoff, Heart, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

export default function HopeAir() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/billing/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#E3E1D9]">
        <button onClick={() => navigate(-1)} data-testid="hope-air-back" className="flex items-center gap-2 text-[#1E2420]">
          <ArrowLeft size={18} /> <span className="text-sm font-medium">Back</span>
        </button>
        <button onClick={() => navigate("/subscribe")} data-testid="hope-air-subscribe" className="text-sm text-[#2C4C3B] hover:underline">
          Subscribe to MedEasy
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C17767]/10 text-[#C17767] text-xs uppercase tracking-[0.18em] font-semibold mb-6">
          <Heart weight="fill" size={12} /> Our charity partner
        </div>

        <h1 className="font-[Manrope] text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
          MedEasy gives 10% of every payment to Hope Air.
        </h1>

        <div className="mt-8 rounded-3xl bg-gradient-to-br from-[#2C4C3B] to-[#3A634D] text-white p-8 flex items-center gap-6 flex-wrap">
          <div className="bg-white rounded-2xl px-5 py-4 shrink-0 shadow-lg">
            <img src="/hopeair-logo.png" alt="Hope Air" className="h-12 w-auto object-contain" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">Our charity partner</div>
            <div className="font-[Manrope] font-bold text-2xl mt-1">Free flights for Canadians who can&apos;t afford medical travel</div>
            <a
              href="https://hopeair.ca"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#D4A373] mt-2 hover:underline"
              data-testid="hope-air-website-link"
            >
              hopeair.ca <ArrowRight size={14} />
            </a>
          </div>
        </div>

        <div className="prose prose-stone mt-10 text-[#1E2420] max-w-none space-y-5">
          <p className="text-lg leading-relaxed">
            MedEasy exists because medical travel is brutal. Long drives, parking stubs, motel
            bills, prescriptions that don't always get covered. Most Canadians can absorb it.
            <strong> But some can't.</strong>
          </p>

          <p className="leading-relaxed">
            <strong>Hope Air</strong> is the Canadian charity I want to support. Since 1986
            they've arranged over <strong>180,000 free flights</strong> for Canadians who
            otherwise couldn't get to specialist appointments, treatments, and surgeries —
            especially folks living in rural and remote communities, where the nearest
            specialist might be a province away.
          </p>

          <p className="leading-relaxed">
            They don't only fly people. Hope Air also helps with <strong>ground travel,
            hotel stays, and meal vouchers</strong> — the same kinds of expenses MedEasy
            helps you track. It felt like the right fit.
          </p>

          <p className="leading-relaxed">
            So every time someone pays for MedEasy, <strong>10% of that payment is set
            aside</strong> and donated to Hope Air. We publish a running total on our
            subscribe page so you can see the impact accumulate.
          </p>

          <p className="leading-relaxed text-[#5C6660] text-sm">
            <em>Note: Hope Air is an independent charity. MedEasy is not affiliated with or
            endorsed by Hope Air — they don't yet know we exist! We're just a small Canadian
            app that thinks they're doing important work.</em>
          </p>
        </div>

        <Card className="mt-12 rounded-2xl border-[#E3E1D9]">
          <CardContent className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6 items-center">
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Our pledge</div>
              <div className="font-[Manrope] font-bold text-2xl sm:text-3xl text-[#1E2420] mt-1">
                10% of every MedEasy subscription
              </div>
              <div className="text-sm text-[#5C6660] mt-2">
                is set aside for Hope Air. We&apos;ll publish a running community total once payments start flowing.
              </div>
            </div>
            <div className="flex sm:justify-end">
              <Button
                onClick={() => navigate("/subscribe")}
                className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white h-11 px-6"
                data-testid="hope-air-cta-subscribe"
              >
                Subscribe — give 10%
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-[#5C6660] mt-8 text-center">
          Want to give more directly? Visit <a href="https://hopeair.ca/donate" target="_blank" rel="noopener noreferrer" className="text-[#2C4C3B] hover:underline">hopeair.ca/donate</a>.
        </p>
      </main>
    </div>
  );
}
